import { useMemo } from "react"
import { Alert, Button, Stack, TextField, Typography } from "@mui/material"
import { StandardSelector } from "lib/@components/combos/standard-selector"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { formatDateTime, formatDateTimeInput, toIsoFromInput } from "../lib/task-version-utils"

export function HistoricalLibraryToolbar({
    baselineCreatedAt,
    checkpoints,
    historicalMode,
    libraryAsOf,
    onClearHistoricalMode,
    onSetLibraryAsOf,
    unsupportedHistoricalView,
    vertical,
}) {
    const checkpointOptions = useMemo(buildCheckpointOptions, [checkpoints])

    return (
        <Stack spacing={2} sx={{ mb: vertical ? 0 : 3 }}>
            <Stack
                direction={vertical ? "column" : { xs: "column", md: "row" }}
                spacing={2}
                alignItems={vertical ? "stretch" : { xs: "stretch", md: "center" }}
            >
                <TextField
                    label="View Library As Of"
                    type="datetime-local"
                    size="small"
                    value={formatDateTimeInput(libraryAsOf)}
                    onChange={handleAsOfChange}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 260 }}
                />

                <StandardSelector
                    options={checkpointOptions}
                    value={selectedCheckpointValue()}
                    onChange={handleCheckpointChange}
                    sortTitle="Checkpoint:"
                    size="small"
                    variant="outlined"
                    sx={{ minWidth: 280, textTransform: "none" }}
                />

                <If value={historicalMode}>
                    <Button variant="outlined" color="primary" onClick={onClearHistoricalMode}>
                        Return To Live Library
                    </Button>
                </If>
            </Stack>

            <If value={historicalMode}>
                <Alert severity={unsupportedHistoricalView ? "warning" : "info"}>
                    <If value={unsupportedHistoricalView}>
                        Historical view is only available from {formatDateTime(baselineCreatedAt)} onwards.
                        <Else>Viewing library as of {formatDateTime(libraryAsOf)}.</Else>
                    </If>
                </Alert>
            </If>

            <If value={!historicalMode && baselineCreatedAt}>
                <Typography variant="caption" color="text.secondary">
                    Historical browsing is available from {formatDateTime(baselineCreatedAt)} onwards.
                </Typography>
            </If>
        </Stack>
    )

    function handleAsOfChange(event) {
        onSetLibraryAsOf(toIsoFromInput(event.target.value))
    }

    function handleCheckpointChange(value) {
        if (value) onSetLibraryAsOf(value)
    }

    function buildCheckpointOptions() {
        const items = (checkpoints || []).map((item) => ({
            value: item.createdAt,
            label: `${item.label} (${formatDateTime(item.createdAt)})`,
        }))
        return [{ value: "", label: "Select checkpoint" }, ...items]
    }

    function selectedCheckpointValue() {
        if (!libraryAsOf) return ""
        const exact = checkpoints?.find((item) => item.createdAt === libraryAsOf)
        if (exact) return exact.createdAt
        const asOfTime = new Date(libraryAsOf).getTime()
        const prior = checkpoints
            ?.filter((item) => new Date(item.createdAt).getTime() <= asOfTime)
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        return prior?.[0]?.createdAt ?? ""
    }
}
